// Utils
import { applyChanges } from './MergeUtil';

// Dernier segment du type : 'element.update' → 'update'
export const kindOf = (type) => type.slice(type.lastIndexOf('.') + 1);

const CREATION_KINDS = ['create', 'bulkCreate', 'upload'];
// Indique si le type crée une entité (les opérations qui la référencent attendent son acceptation)
export const isCreationType = (type) => CREATION_KINDS.includes(kindOf(type));

// Options de deux modifications fusionnées (historique, replantation) : vraies si l'une les demandait
const mergeFlags = (a = {}, b = {}) => ({ ...a, ...b, history: !!(a.history || b.history), replanting: !!(a.replanting || b.replanting) });

// Compactage : { add } pour une nouvelle opération, { update } pour compléter l'opération en attente du même élément
export const planEnqueue = (pendingSameTarget, op) => {
    if (op.type !== 'element.update' || !op.payload?.changes) return { add: op, update: null };
    const candidates = pendingSameTarget.filter(o => ['element.create', 'element.update'].includes(o.type));
    const last = candidates[candidates.length - 1];
    if (!last) return { add: op, update: null };

    if (last.type === 'element.create')
        return { add: null, update: { id: last.id, changes: { payload: { ...last.payload, feature: applyChanges(last.payload.feature, op.payload.changes) } } } };

    return {
        add: null,
        update: {
            id: last.id,
            changes: {
                payload: {
                    ...last.payload,
                    changes: { ...last.payload.changes, ...op.payload.changes },
                    flags: mergeFlags(last.payload.flags, op.payload.flags)
                },
                // La valeur d'origine reste celle d'avant la première saisie
                base: { ...op.base, ...last.base }
            }
        }
    };
};

// Abandon de la création d'entités (clés 'element:…', 'photo:…', 'occurrence:…') : saisie groupée réduite à ce qui ne les vise pas.
// kept : champs de l'opération réduite ; lost : copie de la partie retirée, pour l'annulation à l'écran (onAbandon), null s'il n'y a
// rien à annuler. null si l'opération ne se découpe pas ou ne porte que ces entités : elle part alors en entier
export const withoutEntities = (op, lostKeys) => {
    const isLost = (entity, id) => lostKeys.includes(`${entity}:${id}`);
    const isLinkLost = link => isLost('element', link.elementId) || isLost('element', link.linkedElementId);
    const refs = op.refs.filter(ref => !isLost(ref.entity, ref.id));
    // Liste du payload découpée : null si rien ou tout y est perdu
    const split = (list, isItemLost) => {
        const kept = list.filter(item => !isItemLost(item));
        return kept.length && kept.length < list.length ? kept : null;
    };
    const reduce = (payload, lost = null, lostTouches = []) => ({
        kept: { touches: op.touches.filter(key => !lostKeys.includes(key) && !lostTouches.includes(key)), refs, payload: { ...op.payload, ...payload } },
        lost
    });
    const linkKeys = links => links.filter(isLinkLost).map(link => `link:${link.elementId}:${link.linkedElementId}`);
    switch (op.type) {
        case 'element.bulkUpdate': {
            const items = split(op.payload.items, item => isLost('element', item.id));
            return items && reduce({ items }, { ...op, payload: { ...op.payload, items: op.payload.items.filter(item => isLost('element', item.id)) } });
        }
        case 'element.delete': {
            const features = split(op.payload.features, feature => isLost('element', feature.id));
            return features && reduce({ features }, { ...op, payload: { ...op.payload, features: op.payload.features.filter(feature => isLost('element', feature.id)) } });
        }
        case 'occurrences.update': {
            const occurrences = split(op.payload.occurrences, occurrence => isLost('occurrence', occurrence.id));
            return occurrences && reduce({ occurrences });
        }
        case 'occurrences.delete': {
            const ids = split(op.payload.ids, id => isLost('occurrence', id));
            return ids && reduce({ ids });
        }
        case 'photo.delete': {
            const ids = split(op.payload.ids, id => isLost('photo', id));
            return ids && reduce({ ids });
        }
        case 'event.create': {
            // L'évènement reste, même sans élément
            const projectEventElements = op.payload.projectEvent.projectEventElements.filter(pee => !isLost('element', pee.elementId));
            return projectEventElements.length < op.payload.projectEvent.projectEventElements.length
                ? reduce({ projectEvent: { ...op.payload.projectEvent, projectEventElements } }) : null;
        }
        case 'event.update': {
            // Date, commentaire et liens retirés gardés même sans lien ajouté restant
            const peeToAdd = op.payload.peeToAdd.filter(pee => !isLost('element', pee.elementId));
            const hasOther = peeToAdd.length || op.payload.peeToRemove.length || Object.keys(op.payload.changes).length;
            return peeToAdd.length < op.payload.peeToAdd.length && hasOther ? reduce({ peeToAdd }) : null;
        }
        case 'series.addElements': {
            const elements = split(op.payload.elements, element => isLost('element', element.elementId));
            return elements && reduce({ elements });
        }
        case 'link.toggle': {
            const toAdd = op.payload.toAdd.filter(link => !isLinkLost(link)), toDelete = op.payload.toDelete.filter(link => !isLinkLost(link));
            const total = op.payload.toAdd.length + op.payload.toDelete.length;
            return toAdd.length + toDelete.length && toAdd.length + toDelete.length < total
                ? reduce({ toAdd, toDelete }, null, linkKeys([...op.payload.toAdd, ...op.payload.toDelete])) : null;
        }
        case 'link.delete': {
            const linkedElements = split(op.payload.linkedElements, isLinkLost);
            return linkedElements && reduce({ linkedElements }, null, linkKeys(op.payload.linkedElements));
        }
        default: return null;
    }
};
